import {Button} from "@heroui/react";

import {MobileFooter} from "./mobile-footer";
import {ThemeBuilderProvider, useThemeBuilder} from "./theme-builder-context";
import {ThemeBuilderPanel} from "./theme-builder-panel";
import {ThemeCodePanel} from "./theme-code-panel";

/** Palette SVG icon. */
function PaletteIcon() {
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className="scale-200"
    >
      <path
        d="M12 2C6.48 2 2 6.48 2 12c0 5.52 4.48 10 10 10 .83 0 1.5-.67 1.5-1.5 0-.39-.15-.74-.39-1.01-.23-.26-.38-.61-.38-.99 0-.83.67-1.5 1.5-1.5H16c2.76 0 5-2.24 5-5 0-4.42-4.03-8-9-8z"
        fill="currentColor"
        opacity="0.25"
      />
      <circle cx="6.5" cy="11.5" r="1.5" fill="currentColor" />
      <circle cx="9.5" cy="7.5" r="1.5" fill="currentColor" />
      <circle cx="14.5" cy="7.5" r="1.5" fill="currentColor" />
      <circle cx="17.5" cy="11.5" r="1.5" fill="currentColor" />
    </svg>
  );
}

/** The floating toggle button — consumes the context from the outer provider. */
function ToggleButton() {
  const {isOpen, toggleOpen} = useThemeBuilder();

  return (
    <Button
      isIconOnly
      onPress={toggleOpen}
      aria-label={isOpen ? "Close theme builder" : "Open theme builder"}
      aria-pressed={isOpen}
      className={[
        "fixed right-20 bottom-3 z-50",
        "size-12 rounded-full shadow-lg",
        "transition-all duration-200",
        isOpen ? "hidden" : "",
      ].join(" ")}
      variant="secondary"
    >
      <PaletteIcon />
    </Button>
  );
}

/**
 * Self-contained theme builder entry point.
 * Wraps the provider, floating toggle button and sliding panel.
 * Responsive: shows mobile footer on small screens (sm), desktop layout on medium+ (md).
 * Drop this anywhere in the app tree — no additional setup required.
 *
 * @example
 * // In App.tsx:
 * <ThemeBuilderToggle />
 */
export function ThemeBuilderToggle() {
  return (
    <ThemeBuilderProvider>
      {/* Desktop layout: floating button + bottom panel (hidden on mobile) */}
      <div className="hidden md:block">
        <ToggleButton />
        <ThemeBuilderPanel />
      </div>

      {/* Mobile layout: bottom sheet footer (hidden on desktop) */}
      <div className="md:hidden">
        <MobileFooter />
      </div>

      {/* Code panel — rendered outside breakpoint divs so it works on both */}
      <ThemeCodePanel />
    </ThemeBuilderProvider>
  );
}
